import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, AlertCircle, Truck, ShieldAlert, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useRazorpay } from 'react-razorpay';

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, cartTotal, fetchCart } = useCart();
  const { Razorpay } = useRazorpay();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [showNewAddress, setShowNewAddress] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [rollPressOptions, setRollPressOptions] = useState({});
  const [specialRequest, setSpecialRequest] = useState('');
  const [newAddress, setNewAddress] = useState({
    name: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    is_default: false
  });

  const SHIPPING_THRESHOLD = 5000;
  const SHIPPING_COST = 100;
  const shippingCost = cartTotal >= SHIPPING_THRESHOLD ? 0 : SHIPPING_COST;
  const totalWithShipping = cartTotal + shippingCost;

  useEffect(() => {
    if (cart.length === 0) {
      navigate('/cart');
      return;
    }
    fetchAddresses();
    initializeRollPressOptions();
  }, []);

  const initializeRollPressOptions = () => {
    const options = {};
    cart.forEach(item => {
      const category = item.product?.category;
      if (category === 'Sarees') {
        options[item.product_id] = 'no_rollpress';
      } else if (category === 'Dupattas') {
        options[item.product_id] = 'not_rollpressed';
      } else if (category === 'Suits') {
        options[item.product_id] = 'kurta_bottom_rollpressed';
      }
    });
    setRollPressOptions(options);
  };

  const fetchAddresses = async () => {
    try {
      const response = await api.get('/addresses');
      setAddresses(response.data);
      const defaultAddr = response.data.find(a => a.is_default);
      if (defaultAddr) {
        setSelectedAddress(defaultAddr.id);
      } else if (response.data.length > 0) {
        setSelectedAddress(response.data[0].id);
      } else {
        setShowNewAddress(true);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
    }
  };

  const fetchPincodeData = async (pincode) => {
    if (pincode.length !== 6) return;
    
    try {
      setPincodeLoading(true);
      const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await response.json();
      
      if (data[0]?.Status === 'Success' && data[0]?.PostOffice?.length > 0) {
        const postOffice = data[0].PostOffice[0];
        setNewAddress(prev => ({
          ...prev,
          city: postOffice.District,
          state: postOffice.State
        }));
        toast.success('City and state auto-filled!');
      } else {
        toast.error('Invalid pincode');
      }
    } catch (error) {
      console.error('Pincode API error:', error);
      toast.error('Could not fetch pincode data');
    } finally {
      setPincodeLoading(false);
    }
  };

  const handlePincodeChange = (e) => {
    const pincode = e.target.value;
    setNewAddress({ ...newAddress, pincode });
    if (pincode.length === 6) {
      fetchPincodeData(pincode);
    }
  };

  const handleSaveAddress = async () => {
    if (!newAddress.name || !newAddress.phone || !newAddress.address_line1 || 
        !newAddress.city || !newAddress.state || !newAddress.pincode) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const response = await api.post('/addresses', newAddress);
      setAddresses([...addresses, response.data]);
      setSelectedAddress(response.data.id);
      setShowNewAddress(false);
      toast.success('Address saved!');
    } catch (error) {
      console.error('Address save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save address');
    }
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddress) {
      toast.error('Please select a delivery address');
      return;
    }

    // Validate roll press options for sarees
    const sareeItems = cart.filter(item => item.product?.category === 'Sarees');
    for (const item of sareeItems) {
      if (!rollPressOptions[item.product_id]) {
        toast.error(`Please select roll press option for ${item.product.name}`);
        return;
      }
    }

    try {
      setLoading(true);
      
      const orderItems = cart.map(item => ({
        product_id: item.product_id,
        product_name: item.product.name,
        product_image: item.product.image,
        quantity: item.quantity,
        price: item.product.price,
        roll_press_option: rollPressOptions[item.product_id] || 'not_applicable'
      }));

      const orderResponse = await api.post('/orders/create', {
        items: orderItems,
        total_amount: totalWithShipping,
        address_id: selectedAddress,
        special_request: specialRequest || ''
      });

      const { razorpay_order_id, amount, currency, order_id } = orderResponse.data;

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: amount,
        currency: currency,
        name: 'Bandhani by Abdul Ahad Khatri',
        description: 'Order Payment',
        order_id: razorpay_order_id,
        handler: async (response) => {
          try {
            await api.post('/orders/verify-payment', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order_id: order_id
            });
            
            await fetchCart();
            toast.success('Order placed successfully!');
            navigate('/profile/orders');
          } catch (error) {
            toast.error('Payment verification failed');
            console.error('Payment verification error:', error);
          }
        },
        prefill: {
          name: newAddress.name || addresses.find(a => a.id === selectedAddress)?.name,
          contact: newAddress.phone || addresses.find(a => a.id === selectedAddress)?.phone
        },
        theme: {
          color: '#800000'
        }
      };

      const rzp = new Razorpay(options);
      rzp.on('payment.failed', function (response) {
        toast.error('Payment failed. Please try again.');
      });
      rzp.open();
    } catch (error) {
      toast.error('Failed to create order');
      console.error('Order creation error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-24 md:py-32 max-w-[1400px] mx-auto px-4 md:px-8">
      <h1 className="text-5xl md:text-6xl font-light tracking-tight mb-12" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
        Checkout
      </h1>

      {/* Important Notices */}
      <div className="mb-8 space-y-4">
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg flex items-start gap-3">
          <ShieldAlert className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-900">No Exchange or Returns</p>
            <p className="text-sm text-amber-800">All sales are final. Please choose carefully before placing your order.</p>
          </div>
        </div>
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg flex items-start gap-3">
          <Truck className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-blue-900">Shipping Information</p>
            <p className="text-sm text-blue-800">Delivery in 7 business days. Free shipping on orders above ₹5,000!</p>
          </div>
        </div>
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg flex items-start gap-3 mt-3" data-testid="checkout-return-policy-note">
          <Clock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-900">Return &amp; Cancellation</p>
            <p className="text-sm text-amber-800">
              Cancellation allowed within <span className="font-semibold">24 hours</span> of placing the order.
              After 24 hours, no cancellation, no return, no exchange.{' '}
              <Link to="/return-policy" className="underline font-medium" data-testid="checkout-return-policy-link">
                Read full policy
              </Link>
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-8">
          {/* Roll Press Options */}
          {cart.some(item => ['Sarees', 'Suits'].includes(item.product?.category)) && (
            <div className="border border-border p-6 rounded-xl">
              <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Roll Press Options
              </h3>
              <div className="space-y-6">
                {cart.map(item => {
                  const category = item.product?.category;
                  if (category === 'Sarees') {
                    return (
                      <div key={item.product_id} className="border-b border-border pb-4">
                        <p className="font-medium mb-3">{item.product.name}</p>
                        <RadioGroup 
                          value={rollPressOptions[item.product_id] || ''} 
                          onValueChange={(value) => setRollPressOptions({...rollPressOptions, [item.product_id]: value})}
                        >
                          <div className="flex items-center space-x-2 mb-2">
                            <RadioGroupItem value="fully_rollpressed" id={`${item.product_id}-full`} />
                            <Label htmlFor={`${item.product_id}-full`}>Fully Roll Pressed</Label>
                          </div>
                          <div className="flex items-center space-x-2 mb-2">
                            <RadioGroupItem value="half_rollpressed" id={`${item.product_id}-half`} />
                            <Label htmlFor={`${item.product_id}-half`}>Half Roll Pressed</Label>
                          </div>
                          <div className="flex items-center space-x-2 mb-2">
                            <RadioGroupItem value="no_rollpress" id={`${item.product_id}-no`} />
                            <Label htmlFor={`${item.product_id}-no`}>No Roll Press</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="fully_except_shoulder" id={`${item.product_id}-except`} />
                            <Label htmlFor={`${item.product_id}-except`}>Fully Roll Pressed Except Shoulder</Label>
                          </div>
                        </RadioGroup>
                      </div>
                    );
                  } else if (category === 'Suits') {
                    return (
                      <div key={item.product_id} className="border-b border-border pb-4">
                        <p className="font-medium mb-2">{item.product.name}</p>
                        <p className="text-sm text-muted-foreground">Kurta & Bottom: Roll Pressed | Dupatta: Not Roll Pressed</p>
                      </div>
                    );
                  } else if (category === 'Dupattas') {
                    return (
                      <div key={item.product_id} className="border-b border-border pb-4">
                        <p className="font-medium mb-2">{item.product.name}</p>
                        <p className="text-sm text-muted-foreground">Not Roll Pressed</p>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          )}

          {/* Delivery Address */}
          <div className="border border-border p-6 rounded-xl">
            <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Delivery Address
            </h3>
            
            {addresses.length > 0 && !showNewAddress && (
              <RadioGroup value={selectedAddress} onValueChange={setSelectedAddress} className="space-y-4">
                {addresses.map((addr) => (
                  <div key={addr.id} className="flex items-start space-x-3 p-4 border border-border rounded-lg">
                    <RadioGroupItem value={addr.id} id={addr.id} />
                    <Label htmlFor={addr.id} className="flex-1 cursor-pointer">
                      <p className="font-medium">{addr.name}</p>
                      <p className="text-sm text-muted-foreground">{addr.phone}</p>
                      <p className="text-sm text-muted-foreground">
                        {addr.address_line1}, {addr.address_line2 && `${addr.address_line2}, `}
                        {addr.city}, {addr.state} - {addr.pincode}
                      </p>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}

            {!showNewAddress && (
              <Button 
                variant="outline" 
                className="mt-4 rounded-full"
                onClick={() => setShowNewAddress(true)}
              >
                Add New Address
              </Button>
            )}

            {showNewAddress && (
              <div className="space-y-4 p-6 border border-border rounded-lg" data-testid="checkout-new-address-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="checkout-name" className="text-xs uppercase tracking-widest">Name *</Label>
                    <Input
                      id="checkout-name"
                      name="name"
                      placeholder="Full name"
                      value={newAddress.name}
                      onChange={(e) => setNewAddress({...newAddress, name: e.target.value})}
                      className="rounded-full mt-2"
                      required
                      data-testid="checkout-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="checkout-phone" className="text-xs uppercase tracking-widest">Phone *</Label>
                    <Input
                      id="checkout-phone"
                      name="phone"
                      type="tel"
                      placeholder="10-digit mobile"
                      value={newAddress.phone}
                      onChange={(e) => setNewAddress({...newAddress, phone: e.target.value})}
                      className="rounded-full mt-2"
                      required
                      data-testid="checkout-phone-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="checkout-address1" className="text-xs uppercase tracking-widest">Address Line 1 *</Label>
                  <Input
                    id="checkout-address1"
                    name="address_line1"
                    placeholder="House no., street"
                    value={newAddress.address_line1}
                    onChange={(e) => setNewAddress({...newAddress, address_line1: e.target.value})}
                    className="rounded-full mt-2"
                    required
                    data-testid="checkout-address1-input"
                  />
                </div>
                <div>
                  <Label htmlFor="checkout-address2" className="text-xs uppercase tracking-widest">Address Line 2</Label>
                  <Input
                    id="checkout-address2"
                    name="address_line2"
                    placeholder="Landmark, area (optional)"
                    value={newAddress.address_line2}
                    onChange={(e) => setNewAddress({...newAddress, address_line2: e.target.value})}
                    className="rounded-full mt-2"
                    data-testid="checkout-address2-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="checkout-pincode" className="text-xs uppercase tracking-widest">Pincode *</Label>
                    <div className="relative">
                      <Input
                        id="checkout-pincode"
                        name="pincode"
                        inputMode="numeric"
                        placeholder="6-digit pincode"
                        value={newAddress.pincode}
                        onChange={handlePincodeChange}
                        className="rounded-full mt-2"
                        maxLength={6}
                        required
                        data-testid="checkout-pincode-input"
                      />
                      {pincodeLoading && (
                        <Loader2 className="absolute right-3 top-5 h-4 w-4 animate-spin" />
                      )}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="checkout-city" className="text-xs uppercase tracking-widest">City *</Label>
                    <Input
                      id="checkout-city"
                      name="city"
                      placeholder="City"
                      value={newAddress.city}
                      onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                      className="rounded-full mt-2"
                      required
                      data-testid="checkout-city-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="checkout-state" className="text-xs uppercase tracking-widest">State *</Label>
                    <Input
                      id="checkout-state"
                      name="state"
                      placeholder="State"
                      value={newAddress.state}
                      onChange={(e) => setNewAddress({...newAddress, state: e.target.value})}
                      className="rounded-full mt-2"
                      required
                      data-testid="checkout-state-input"
                    />
                  </div>
                </div>
                <div className="flex gap-4">
                  <Button onClick={handleSaveAddress} className="rounded-full" data-testid="checkout-save-address-button">
                    Save Address
                  </Button>
                  {addresses.length > 0 && (
                    <Button variant="outline" onClick={() => setShowNewAddress(false)} className="rounded-full" data-testid="checkout-cancel-address-button">
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Special Request */}
          <div className="border border-border p-6 rounded-xl">
            <h3 className="text-2xl font-medium mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Special Request
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              Any special instructions for your order? (e.g., gift wrapping, specific color preferences, delivery notes)
            </p>
            <textarea
              value={specialRequest}
              onChange={(e) => setSpecialRequest(e.target.value)}
              className="w-full rounded-xl p-4 border border-input bg-background min-h-[100px] text-sm"
              placeholder="Type your special request here..."
              maxLength={500}
              data-testid="special-request-input"
            />
            <p className="text-xs text-muted-foreground mt-1 text-right">{specialRequest.length}/500</p>
          </div>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-secondary p-8 sticky top-24 rounded-xl">
            <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Order Summary
            </h3>
            <div className="space-y-4 mb-6">
              {cart.map((item) => (
                <div key={item.product_id} className="flex justify-between text-sm">
                  <span>{item.product.name} x {item.quantity}</span>
                  <span>₹{(item.product.price * item.quantity).toLocaleString('en-IN')}</span>
                </div>
              ))}
              <div className="border-t border-border pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>₹{cartTotal.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Shipping</span>
                  <span className={shippingCost === 0 ? 'text-green-600 font-medium' : ''}>
                    {shippingCost === 0 ? 'FREE' : `₹${shippingCost}`}
                  </span>
                </div>
                {cartTotal < SHIPPING_THRESHOLD && (
                  <p className="text-xs text-muted-foreground">
                    Add ₹{(SHIPPING_THRESHOLD - cartTotal).toLocaleString('en-IN')} more for free shipping!
                  </p>
                )}
              </div>
              <div className="border-t border-border pt-4 flex justify-between text-xl font-light">
                <span>Total</span>
                <span>₹{totalWithShipping.toLocaleString('en-IN')}</span>
              </div>
            </div>
            {/* Stock Availability Warning */}
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-4" data-testid="stock-warning">
              <p className="text-sm text-amber-800 font-medium text-center">
                Please contact us on <a href="tel:7621003708" className="underline font-bold">7621003708</a> for stock availability before placing your order.
              </p>
            </div>
            <Button 
              onClick={handlePlaceOrder}
              disabled={loading || !selectedAddress}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 uppercase tracking-widest text-sm font-medium"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                'Place Order'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;